import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, ShoppingBag, Users, IndianRupee, Clock } from 'lucide-react';
import { API } from '../../App';

const AdminDashboard = ({ adminToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const stats = [
    { label: 'Total Products', value: analytics?.total_products || 0, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Total Orders', value: analytics?.total_orders || 0, icon: ShoppingBag, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Total Customers', value: analytics?.total_customers || 0, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Total Revenue', value: `\u20B9${(analytics?.total_revenue || 0).toFixed(2)}`, icon: IndianRupee, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Pending Orders', value: analytics?.pending_orders || 0, icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' }
  ];

  return (
    <div className="py-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your B2B platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white border border-border p-6"
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.bg} flex items-center justify-center`}>
                <stat.icon size={24} className={stat.color} />
              </div>
            </div>
            <p className="text-3xl font-bold mb-1">{stat.value}</p>
            <p className="text-sm text-muted-foreground">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-white border border-border p-6">
        <h2 className="text-2xl font-heading font-semibold mb-6">Recent Orders</h2>
        {analytics?.recent_orders && analytics.recent_orders.length > 0 ? (
          <div className="space-y-4">
            {analytics.recent_orders.map((order) => (
              <div key={order.id} className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <p className="font-semibold">Order #{order.order_number}</p>
                  <p className="text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary">&#8377;{order.total_amount.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">{order.order_status}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No orders yet</p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        <Link to="/admin/products" className="bg-primary text-primary-foreground p-6 hover:bg-primary/90 transition-colors">
          <Package size={32} className="mb-3" />
          <p className="font-semibold">Manage Products</p>
        </Link>
        <Link to="/admin/orders" className="bg-secondary text-secondary-foreground p-6 hover:bg-secondary/90 transition-colors">
          <ShoppingBag size={32} className="mb-3" />
          <p className="font-semibold">Manage Orders</p>
        </Link>
        <Link to="/admin/customers" className="bg-primary text-primary-foreground p-6 hover:bg-primary/90 transition-colors">
          <Users size={32} className="mb-3" />
          <p className="font-semibold">View Customers</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
